import type {
  ContactAgentMessage,
  EmailModalFormValues,
  PrescreenMessageData,
} from '../email-modal/types'
import type { PrescreenFieldKeys, PrescreenFields } from '../prescreen-form'
import type { UserProfile } from 'store/users/types'

import { buildPath } from '@rentspree/path'
import { set } from 'lodash'
import { useContext, useEffect, useState } from 'react'

import { Body, ButtonLink, Text } from 'components'
import { PRESCREEN_FIELDS } from 'constant'
import { ListingIdContext } from 'context/listingIdContext'
import env from 'env'
import { useDispatch, useSelector } from 'store'
import { selectAgentProfile } from 'store/agent-profile'
import { selectPublicListingDataToRCM } from 'store/public-listing'
import { selectPublicMessagingApiStatus } from 'store/public-messaging'
import { asyncActions } from 'store/public-messaging/async-actions'
import { actions as toastActions } from 'store/toast'
import { selectUserProfile, selectUserProfileUserType } from 'store/users'
import tracker, { events } from 'tracker'

import { Wizard } from '../../../../wizard'
import { WizardSubSectionText } from '../../../../wizard/wizard.styled'
import {
  MESSAGE_TYPE,
  openSweetAlertForAnonymousUserPrescreen,
  openSweetAlertForLoggedIn,
} from '../email-modal/constants'
import { EmailModalFooter } from '../email-modal/footer'
import { ContactAgentEmailForm } from '../email-modal/form'
import { ContactAgentInformationAlert } from '../email-modal/style'
import { PrescreenForm } from '../prescreen-form'

import { WizardWrapper } from './contact-agent-prescreen-wizard.styled'

interface Props {
  agentFullName: string
  defaultMessage: string
  onModalClose?: () => void
  modalContentStyle?: IElement
  property?: {
    address: string
    id: string
  }
  profile?: UserProfile
}

export const ContactAgentPrescreenWizard: React.FC<Props> = (props) => {
  const {
    agentFullName,
    defaultMessage,
    onModalClose = () => {
      /* empty function */
    },
    modalContentStyle = {},
    property = undefined,
    profile = {},
  } = props
  const dispatch = useDispatch()
  const [step, setStep] = useState<number>(1)
  const [formData, setFormData] = useState<EmailModalFormValues>()
  const [emailApiRequestSent, setEmailApiRequestSent] = useState(false)
  const { id: renterId } = useSelector(selectUserProfile) || {}
  const { userId, email: agentEmail } = useSelector(selectAgentProfile) || {}
  const publicListingDataToRCM = useSelector(selectPublicListingDataToRCM)
  const sendEmailApiStatus = useSelector(selectPublicMessagingApiStatus)
  const userType = useSelector(selectUserProfileUserType)
  const listingId = useContext(ListingIdContext)
  const buttonLocation = 'LS public page'
  const eventUserState = !renterId ? 'anonymous' : 'loggedin'
  const showReceivedEmailPolicy = (formData?.phone ?? '').length > 0

  useEffect(() => {
    const openSweetAlert = (): void => {
      if (renterId) openSweetAlertForLoggedIn(buttonLocation, userType)
      else openSweetAlertForAnonymousUserPrescreen(buttonLocation, '')
    }

    if (emailApiRequestSent) {
      switch (sendEmailApiStatus) {
        case 'fulfilled':
          onModalClose()
          openSweetAlert()
          break
        case 'rejected':
          onModalClose()
          dispatch(
            toastActions.addToast({
              status: 'error',
              titleMessage: 'Something went wrong while sending message. Please try again.',
              bodyMessage: '',
            }),
          )
          break
        default:
          break
      }
    }
  }, [dispatch, emailApiRequestSent, onModalClose, renterId, sendEmailApiStatus, userType])

  const handleNextStep = () => {
    setStep(step + 1)
  }

  const handlePreviousStep = () => {
    setStep(step - 1)
  }

  const parsePrescreenFormData = (prescreenFormData: PrescreenFields) =>
    Object.keys(PRESCREEN_FIELDS).reduce((result, key) => {
      const prescreenKey = key as PrescreenFieldKeys
      const prescreenAnswer = prescreenFormData[prescreenKey]

      if (prescreenAnswer) {
        const answerSheetData = {
          question: PRESCREEN_FIELDS[prescreenKey].label,
          answer: prescreenAnswer,
        }
        if (!result.answerSheet) {
          set(result, 'answerSheet', [answerSheetData])
        } else {
          result.answerSheet?.push(answerSheetData)
        }
      }
      return result
    }, {} as PrescreenMessageData)

  const createPayload = (prescreenMessageData: PrescreenMessageData) => {
    if (formData) {
      const { fullName, email, phone, message } = formData
      const textMessage = message || defaultMessage
      const messages: ContactAgentMessage[] = [{ data: textMessage, type: MESSAGE_TYPE.MESSAGE }]

      if (prescreenMessageData.answerSheet) {
        messages.push({
          data: { ...prescreenMessageData, propertyId: property?.id || '' },
          type: MESSAGE_TYPE.PRE_SCREEN,
        })
      }

      return {
        sender: {
          fullName,
          email,
          phone: phone || undefined,
        },
        recipient: {
          _id: userId || '',
          email: agentEmail || '',
        },
        messages,
        property: publicListingDataToRCM,
      }
    }
    return undefined
  }

  const handleSubmitStep1 = (emailFormData: EmailModalFormValues) => {
    tracker?.trackEvent(events.LISTING_PUBLIC_PAGE.CLICK_CONTINUE_ON_CONTACT_MODAL, {
      state: eventUserState,
      listing_id: listingId,
    })
    setFormData(emailFormData)
    handleNextStep()
  }

  const handleSubmitStep2 = (prescreenFormData: PrescreenFields) => {
    tracker?.trackEvent(events.PUBLIC_PAGE.CLICK_SEND_MESSAGE, {
      state: eventUserState,
      button_location: buttonLocation,
      listing_id: listingId,
    })

    const prescreenMessageData = parsePrescreenFormData(prescreenFormData)
    const payload = createPayload(prescreenMessageData)

    if (prescreenMessageData.answerSheet) {
      tracker?.trackEvent(events.LISTING_PUBLIC_PAGE.SUBMIT_PRE_SCREEN, {
        state: eventUserState,
        Q1: prescreenFormData.moveInDate ? 'Y' : 'N',
        Q2: prescreenFormData.preferredLeaseTerm ? 'Y' : 'N',
        Q3: prescreenFormData.householdMonthlyIncome ? 'Y' : 'N',
        Q4: prescreenFormData.occupy ? 'Y' : 'N',
        listing_id: listingId,
      })
    }

    if (payload) {
      void dispatch(
        asyncActions.sendContactWithPrescreenToAgent({
          ...payload,
          source: 'listing-syndication',
        }),
      )
      setEmailApiRequestSent(true)
    }
  }

  const redirectToLogin = () => {
    window.location.assign(
      buildPath(`${env.BASE_URL}/account/login`, {}, { continue: window.location.href }),
    )
  }
  const subSection = () => (
    <>
      <WizardSubSectionText $hide={step === 1}>
        Let your agent know more about you <Text>(optional)</Text>
      </WizardSubSectionText>
      {!renterId && (
        <ContactAgentInformationAlert variant="info" noClose $mMargin="0 0 24px">
          <Body $textStyle="body-small" $mTextStyle="body" $block>
            Already have an account?{' '}
            <ButtonLink
              text="Login"
              $textStyle="body-small"
              $mTextStyle="body"
              $bold
              $underline
              onClick={redirectToLogin}
            />{' '}
            to chat via direct messages.
          </Body>
        </ContactAgentInformationAlert>
      )}
    </>
  )

  return (
    <WizardWrapper {...modalContentStyle}>
      <Wizard step={step} title={`Contact ${agentFullName}`} subSection={subSection()}>
        <ContactAgentEmailForm
          handleSubmit={handleSubmitStep1}
          loading={sendEmailApiStatus === 'pending'}
          defaultMessage={defaultMessage}
          profile={profile}
          prevFormData={formData}
        />
        <>
          <PrescreenForm onSubmit={handleSubmitStep2} onClickPrevious={handlePreviousStep} />
          <EmailModalFooter hasTerm={showReceivedEmailPolicy} />
        </>
      </Wizard>
    </WizardWrapper>
  )
}
